import {
  calculateTotalPricingBySpecialty,
  extractUniqueSpecialties,
  groupCptCodesBySpecialty,
  sortSpecialtiesByPricing,
} from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useMemo } from "react";
import { useGetPrecalculatedAllowablesQuery } from "store/queries/pricing";
import styled from "styled-components";

import {
  IClaim,
  IInpatientOutlierCalculation,
  ILesserOfChargesCalculation,
  ILineItem,
} from "../../types/claim";
import type {
  ICalculatedData,
  IProviderProcedureData,
} from "../../types/precalculatedAllowables.types";
import { ErrorMessage } from "./ErrorMessage";
import { NoDataMessage } from "./NoDataMessage";
import { PrimaryPricingBlock } from "./PrimaryPricingBlock";
import {
  descriptionGroupKey,
  parseOrdinalProcedurePrefix,
  SpecialtyTabContent,
} from "./SpecialtyTabContent";

interface IPassThroughCalculation {
  type: string;
  totalPassThroughAmountInCents: number;
  codes: {
    code: string;
    inpatientRulesetId: string;
    chargeMultiplier: number;
    thresholdInCents: number;
    chargeInCents: number;
    calculatedAmountInCents: number;
  }[];
}

interface ILosOutlierCalculation {
  pricingRule: string;
  ruleApplied: boolean;
  ruleStatus: string;
  drgBaseRateInCents: number;
  alos: number | null;
  los: number | null;
  losSource: string | null;
  threshold?: number | null;
  multiplier?: number;
  outlierDays?: number;
  perDiemInCents?: number;
  outlierAddOnInCents?: number;
  expectedAllowedInCents?: number;
  dischargeStatus?: string;
  transferPaymentInCents?: number;
  drgCode?: string;
}

interface IPrecalculatedAllowablesContentProps {
  accountId: string;
  payerId: number;
  caseSignature: string;
  /**
   * CLE-2299: pass the claim's date of service so the lookup prefers the date-specific
   * precalc row over a stale canonical (NULL-serviceDate) row. Without this the tab can
   * silently show pricing that disagrees with the claim's own expectedAmount after a
   * config-driven repricing.
   */
  dateOfService?: string;
  skip?: boolean; // Optional skip flag for controlling when to fetch
  claim?: IClaim; // Optional claim data for NCCI bypass display
  lineItems?: ILineItem[]; // Optional line items from the claim, displayed in Claim Allowables table
  outlierPaymentInCents?: number | null; // High-cost outlier payment to include in totals
  outlierCalculation?: {
    inpatientRulesetId?: string;
    type?: string;
    costOutlierMethod?: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  /** Pass-through calculation data for codes without fee schedule match */
  passThroughCalculation?: IPassThroughCalculation | null;
  /** Total charge amount from the claim (cents), used for outlier display */
  chargeAmountInCents?: number | null;
  /** LOS outlier / transfer calculation data from DRG pricing */
  losOutlierCalculation?: ILosOutlierCalculation | null;
  /** Stop loss payment amount (CLE-1897) */
  stopLossPaymentInCents?: number | null;
  /** Stop loss calculation details (CLE-1897) */
  stopLossCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdMultiplier: number;
    paymentPercent: number;
    thresholdAmountInCents: number;
    chargeAmountInCents: number;
  } | null;
  /** Charge outlier payment amount (CLE-2120) */
  chargeOutlierPaymentInCents?: number | null;
  /** Charge outlier calculation details (CLE-2120) */
  chargeOutlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdType: string;
    thresholdAmountInCents: number;
    outlierPercent: number;
    chargeFloorInCents?: number | null;
    chargeAmountInCents: number;
    edi837RawBaseExpected: number;
    excessChargesInCents?: number;
    chargeOutlierPaymentInCents?: number;
  } | null;
  /** Inpatient outlier — composable trigger/formula/mode rule engine (CLE-2120 v3) */
  inpatientOutlierCalculation?: IInpatientOutlierCalculation | null;
  /** Lesser-of-charges cap — applied last, caps expected at billed charges × multiplier */
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const MissingCodesSection = styled.div`
  padding: 16px 20px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  margin-bottom: 16px;
`;

const MissingCodesTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
`;

const MissingCodesText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const ClaimAllowablesSection = styled.div`
  padding: 12px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
`;

const ClaimAllowablesTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

/**
 * Format specialty name from CONSTANT_CASE to Title Case
 * E.g., "PRIMARY_PROVIDER" -> "Primary Provider"
 */
function formatSpecialtyName(specialty: string): string {
  return specialty
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Reusable content component for displaying precalculated allowables data
 * Can be used standalone or within a modal
 */
export function PrecalculatedAllowablesContent({
  accountId,
  payerId,
  caseSignature,
  dateOfService,
  skip = false,
  claim,
  lineItems,
  outlierPaymentInCents,
  outlierCalculation,
  passThroughCalculation,
  chargeAmountInCents,
  losOutlierCalculation,
  stopLossPaymentInCents,
  stopLossCalculation,
  chargeOutlierPaymentInCents,
  chargeOutlierCalculation,
  inpatientOutlierCalculation,
  lesserOfChargesCalculation,
}: IPrecalculatedAllowablesContentProps) {
  // PRIMARY-mode lesser-of-charges is the sole pricing source for this claim —
  // no fee schedule exists, so the precalc lookup is expected to 404. Bypass the
  // fetch (so the ErrorMessage retry UI never appears) and render the rule block
  // directly below, before any of the loading/error/empty branches.
  const isPrimaryLesserOfCharges =
    lesserOfChargesCalculation?.mode === "PRIMARY";

  // Fetch data using RTK Query hook
  const { data, isLoading, error, refetch } =
    useGetPrecalculatedAllowablesQuery(
      {
        accountId,
        payerId,
        caseSignature,
        dateOfService,
      },
      {
        skip:
          skip ||
          isPrimaryLesserOfCharges ||
          !accountId ||
          !payerId ||
          !caseSignature,
      }
    );

  // Handler for retry button
  const handleRetry = () => {
    refetch();
  };

  // Prefer claim-scoped precalc (e.g. NCCI bypass breakdown from GET/PATCH) over the generic pricing fetch.
  // CLE-2178: this calculatedData is computed server-side WITH the claim's CPT modifiers fed into the
  // pricing engine, so finalAmountInCents and the per-procedure `adjustments` already reflect them
  // (a "Modifier" adjustment line appears alongside NCCI/MPDR/etc.). No client-side re-derivation.
  const effectiveCalculatedData =
    claim?.precalculatedAllowable?.calculatedData ?? data?.data?.calculatedData;

  const effectiveMissingCodes =
    claim?.precalculatedAllowable?.missingCodes ?? data?.data?.missingCodes;

  // Transform data: extract specialties, group by specialty, calculate pricing, sort
  const { sortedSpecialties, specialtyGroupedData, totalAllowable } =
    useMemo(() => {
      if (!effectiveCalculatedData) {
        return {
          sortedSpecialties: [],
          specialtyGroupedData: new Map(),
          totalAllowable: 0,
        };
      }

      const calculatedData = effectiveCalculatedData as ICalculatedData;
      const specialties = extractUniqueSpecialties(calculatedData);
      const grouped = groupCptCodesBySpecialty(calculatedData);
      const pricing = calculateTotalPricingBySpecialty(calculatedData);
      const sorted = sortSpecialtiesByPricing(specialties, pricing);

      // When NCCI bypass is enabled, use the claim's expectedAmount from the backend
      // This already includes the bypassed amounts
      if (claim?.ncciBypassEnabled && claim?.expectedAmount) {
        return {
          sortedSpecialties: sorted,
          specialtyGroupedData: grouped,
          totalAllowable: claim.expectedAmount,
        };
      }

      // Calculate total allowable across ALL specialties
      // For each CPT code, group by specialty and use only the HIGHEST-priced provider per specialty
      const total = calculatedData.cptCodes.reduce((sum, cptCode) => {
        const procedures = cptCode.providerProcedures || [];

        // Group procedures by specialty
        const bySpecialty = new Map<string, typeof procedures>();
        procedures.forEach((pp) => {
          const { specialty } = pp.provider;
          let group = bySpecialty.get(specialty);
          if (!group) {
            group = [];
            bySpecialty.set(specialty, group);
          }
          group.push(pp);
        });

        // For each specialty, find the highest-priced provider
        let cptTotal = 0;
        bySpecialty.forEach((specialtyProviders) => {
          const highestPriced = specialtyProviders.reduce((max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          });
          cptTotal += highestPriced.finalAmountInCents;
        });

        return sum + cptTotal;
      }, 0);

      // When LOS/transfer pricing applies, the expected amount is overridden
      const totalBeforeLos =
        total + (passThroughCalculation?.totalPassThroughAmountInCents ?? 0);
      const finalTotal = losOutlierCalculation?.ruleApplied
        ? (losOutlierCalculation.expectedAllowedInCents ?? 0) +
          (passThroughCalculation?.totalPassThroughAmountInCents ?? 0)
        : totalBeforeLos;

      return {
        sortedSpecialties: sorted,
        specialtyGroupedData: grouped,
        totalAllowable: finalTotal,
      };
    }, [
      effectiveCalculatedData,
      passThroughCalculation,
      losOutlierCalculation,
      claim,
    ]);

  // Format currency helper
  const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  // Negative values render in parens, e.g. ($127.98) — matches the ticket mock
  const formatVariance = (cents: number | null): string => {
    if (cents == null) return "-";
    if (cents < 0) return `(${formatCurrency(Math.abs(cents))})`;
    return formatCurrency(cents);
  };

  // For each CPT code, pick the highest-priced provider procedure across all
  // specialties — mirrors the total-allowable picking logic above and gives
  // us one rate/method/expected per CPT code for the claim allowables table.
  const cptCodeToTopProcedure = useMemo(() => {
    const map = new Map<string, IProviderProcedureData>();
    if (!effectiveCalculatedData) return map;
    const calculatedData = effectiveCalculatedData as ICalculatedData;
    calculatedData.cptCodes.forEach((cpt) => {
      const procs = cpt.providerProcedures ?? [];
      if (procs.length === 0) return;
      const top = procs.reduce((max, pp) =>
        pp.finalAmountInCents > max.finalAmountInCents ? pp : max
      );
      map.set(cpt.code, top);
    });
    return map;
  }, [effectiveCalculatedData]);

  // Per-CPT total billed units, used to pro-rate the cpt-level expected total
  // across each underlying line item. Without this, multiple line items for
  // the same CPT (e.g. 13160 with one units=1 line and one units=266 line)
  // would each display the FULL cpt total, double-counting it visually.
  const cptCodeToTotalQuantity = useMemo(() => {
    const map = new Map<string, number>();
    if (!effectiveCalculatedData) return map;
    const calculatedData = effectiveCalculatedData as ICalculatedData;
    calculatedData.cptCodes.forEach((cpt) => {
      map.set(cpt.code, cpt.quantity);
    });
    return map;
  }, [effectiveCalculatedData]);

  // Pro-rate the per-claim sequestration discount (CLE-2295) across line expecteds in
  // proportion to each line's pre-sequestration expected. Without this the per-row Expected
  // would still read at the pre-sequestration contracted rate, leaving the per-row Variance
  // disagreeing with the claim's overall Difference. Computed once over the full row set
  // (not in-map) because each line's share depends on the un-discounted total.
  const lineSequestrationDiscounts = useMemo(() => {
    const discount = claim?.sequestrationDiscountInCents ?? 0;
    const result = new Map<number, number>();
    if (!lineItems || discount <= 0) return result;
    const undiscounted: Array<{ idx: number; expectedInCents: number }> = [];
    lineItems.forEach((li, idx) => {
      const top = cptCodeToTopProcedure.get(li.cptCode);
      if (!top) return;
      const cptTotalUnits = cptCodeToTotalQuantity.get(li.cptCode) ?? 0;
      const lineUnits = Math.max(
        Number(li.billedUnits) || 0,
        Number(li.quantity) || 0
      );
      const expectedInCents =
        cptTotalUnits > 0 && lineUnits > 0
          ? Math.round((top.finalAmountInCents * lineUnits) / cptTotalUnits)
          : top.finalAmountInCents;
      if (expectedInCents > 0) undiscounted.push({ idx, expectedInCents });
    });
    const totalUndiscounted = undiscounted.reduce(
      (s, r) => s + r.expectedInCents,
      0
    );
    if (totalUndiscounted <= 0) return result;
    let remaining = discount;
    undiscounted.forEach((row, i) => {
      const isLast = i === undiscounted.length - 1;
      // Last row absorbs any rounding remainder so sum(per-row discount) === total discount,
      // keeping sum(per-row Expected) === claim.expectedAmount exactly.
      const lineDiscount = isLast
        ? remaining
        : Math.round((discount * row.expectedInCents) / totalUndiscounted);
      remaining -= lineDiscount;
      result.set(row.idx, lineDiscount);
    });
    return result;
  }, [
    claim?.sequestrationDiscountInCents,
    lineItems,
    cptCodeToTopProcedure,
    cptCodeToTotalQuantity,
  ]);

  const allowableRows = useMemo(() => {
    if (!lineItems) return [];
    return lineItems.map((li, idx) => {
      const top = cptCodeToTopProcedure.get(li.cptCode);
      const contractedRateInCents = top?.priceInCents ?? null;
      // Pro-rate the cpt-level expected by this line's share of total units.
      // Falls back to the cpt-level total when units data is missing or the
      // cpt has only one billed line (the common case — ratio is 1).
      const cptTotalUnits = cptCodeToTotalQuantity.get(li.cptCode) ?? 0;
      const lineUnits = Math.max(
        Number(li.billedUnits) || 0,
        Number(li.quantity) || 0
      );
      let expectedInCents: number | null = null;
      if (top) {
        if (cptTotalUnits > 0 && lineUnits > 0) {
          expectedInCents = Math.round(
            (top.finalAmountInCents * lineUnits) / cptTotalUnits
          );
        } else {
          expectedInCents = top.finalAmountInCents;
        }
      }
      // CLE-2295: subtract this line's share of the claim-level sequestration discount.
      // No-op when the rule didn't apply (map is empty); otherwise the Variance column
      // below reconciles with the claim's overall Difference shown in Financial Summary.
      if (expectedInCents != null && lineSequestrationDiscounts.has(idx)) {
        expectedInCents -= lineSequestrationDiscounts.get(idx) ?? 0;
      }
      // Dedupe method descriptions so multi-unit lines (e.g. 13160 with 266
      // adjustments, or NCCI codes with the same edit applied per unit) show
      // each distinct method once. MPR-style descriptions are normalized by
      // stripping the "Nth procedure" ordinal so 2nd-Nth collapse together.
      const seenKeys = new Set<string>();
      const methodParts: string[] = [];
      (top?.adjustments ?? []).forEach((adj) => {
        const desc = adj.description;
        if (!desc) return;
        const key = descriptionGroupKey(desc);
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        const ord = parseOrdinalProcedurePrefix(desc);
        if (ord) {
          // " is discounted by 50%, ..." → "Discounted by 50%, ..."
          const rest = ord.rest.replace(/^\s+is\s+/i, " ").trimStart();
          methodParts.push(rest.charAt(0).toUpperCase() + rest.slice(1));
        } else {
          methodParts.push(desc);
        }
      });
      // Note: modifier-based adjustments (CLE-2178) are applied upstream in
      // `effectiveCalculatedData`, so `top.finalAmountInCents` is already
      // modifier-adjusted and the modifier note is already in `top.adjustments`.
      let method: string | null = null;
      if (top) {
        method =
          methodParts.length > 0 ? methodParts.join("; ") : "Paid at 100%";
      }
      const allowedInCents = Math.round((li.lineAllowed ?? 0) * 100);
      const varianceInCents =
        expectedInCents != null ? allowedInCents - expectedInCents : null;
      return {
        key: `${li.cptCode}-${li.modifier ?? ""}-${
          li.lineControlNumber ?? idx
        }`,
        cptCode: li.cptCode,
        modifier: li.modifier,
        units: li.billedUnits,
        allowedInCents,
        contractedRateInCents,
        method,
        expectedInCents,
        varianceInCents,
      };
    });
  }, [
    lineItems,
    cptCodeToTopProcedure,
    cptCodeToTotalQuantity,
    lineSequestrationDiscounts,
  ]);

  // PRIMARY-mode lesser-of-charges short-circuit: the rule IS the pricing for
  // this claim (no fee schedule exists), so a precalc 404 is the expected case.
  // Skip every loading / error / empty / success branch below and render the
  // rule block directly. The precalc fetch above is also skipped, so there is
  // nothing in flight to wait on.
  if (isPrimaryLesserOfCharges && lesserOfChargesCalculation) {
    return <PrimaryPricingBlock calculation={lesserOfChargesCalculation} />;
  }

  return (
    <>
      {isLoading && (
        <LoadingContainer>
          <ProgressSpinner />
        </LoadingContainer>
      )}

      {/* Error state: API request failed */}
      {!isLoading && error && (
        <ErrorMessage error={error} onRetry={handleRetry} />
      )}

      {/* No data state: API returned successfully but no data or empty specialties.
          If pass-through data exists, we fall through to the success state below. */}
      {!isLoading &&
        !error &&
        (!effectiveCalculatedData || sortedSpecialties.length === 0) &&
        !passThroughCalculation && <NoDataMessage />}

      {/* Pass-through only state: no fee schedule data, but pass-through exists */}
      {!isLoading &&
        !error &&
        sortedSpecialties.length === 0 &&
        passThroughCalculation && (
          <>
            <TotalAllowableSection>
              <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
              <TotalAllowableAmount>
                {formatCurrency(
                  (passThroughCalculation.totalPassThroughAmountInCents ?? 0) +
                    (outlierPaymentInCents ?? 0)
                )}
              </TotalAllowableAmount>
            </TotalAllowableSection>
            <SpecialtyTabContent
              cptCodes={[]}
              specialty=""
              claim={claim}
              lineItems={lineItems}
              outlierPaymentInCents={outlierPaymentInCents}
              outlierCalculation={outlierCalculation}
              passThroughCalculation={passThroughCalculation}
              chargeAmountInCents={chargeAmountInCents}
              losOutlierCalculation={losOutlierCalculation}
              stopLossPaymentInCents={stopLossPaymentInCents}
              stopLossCalculation={stopLossCalculation}
              chargeOutlierPaymentInCents={chargeOutlierPaymentInCents}
              chargeOutlierCalculation={chargeOutlierCalculation}
              inpatientOutlierCalculation={inpatientOutlierCalculation}
              lesserOfChargesCalculation={lesserOfChargesCalculation}
              sequestrationDiscountInCents={claim?.sequestrationDiscountInCents}
            />
          </>
        )}

      {/* Success state: Display total and tabs with data */}
      {!isLoading &&
        !error &&
        effectiveCalculatedData &&
        sortedSpecialties.length > 0 && (
          <>
            {/* Total Allowable Section */}
            <TotalAllowableSection>
              <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
              <TotalAllowableAmount>
                {formatCurrency(
                  totalAllowable +
                    (outlierPaymentInCents ?? 0) -
                    // CLE-2295: subtract the per-claim Medicare/MA sequestration discount when
                    // applied server-side, so the displayed header total matches the claim's
                    // post-sequestration expectedAmount.
                    (claim?.sequestrationDiscountInCents ?? 0)
                )}
              </TotalAllowableAmount>
            </TotalAllowableSection>

            {/* Missing Codes Warning (if any) — exclude codes handled by pass-through */}
            {(() => {
              const passThroughCodes = new Set(
                passThroughCalculation?.codes.map((c) => c.code) ?? []
              );
              const trulyMissing = (effectiveMissingCodes ?? []).filter(
                (mc) => !passThroughCodes.has(mc.code)
              );
              return trulyMissing.length > 0 ? (
                <MissingCodesSection>
                  <MissingCodesTitle>
                    Incomplete Calculation - Missing Procedure Codes
                  </MissingCodesTitle>
                  <MissingCodesText>
                    The following procedure codes from the case signature were
                    not found in our database and are NOT included in the total
                    allowable above:{" "}
                    <strong>
                      {trulyMissing
                        .map((mc) => `${mc.code} (qty: ${mc.quantity})`)
                        .join(", ")}
                    </strong>
                  </MissingCodesText>
                </MissingCodesSection>
              ) : null;
            })()}

            {/* Specialty Tabs */}
            <TabView>
              {sortedSpecialties.map((specialty, idx) => {
                const cptCodes = specialtyGroupedData.get(specialty) || [];
                return (
                  <TabPanel
                    key={specialty}
                    header={formatSpecialtyName(specialty)}
                  >
                    <SpecialtyTabContent
                      cptCodes={cptCodes}
                      specialty={specialty}
                      claim={claim}
                      lineItems={lineItems}
                      outlierPaymentInCents={outlierPaymentInCents}
                      outlierCalculation={outlierCalculation}
                      // Pass-through and LOS adjustment only on first tab to avoid double-counting
                      passThroughCalculation={
                        idx === 0 ? passThroughCalculation : undefined
                      }
                      chargeAmountInCents={
                        idx === 0 ? chargeAmountInCents : undefined
                      }
                      losOutlierCalculation={
                        idx === 0 ? losOutlierCalculation : undefined
                      }
                      stopLossPaymentInCents={
                        idx === 0 ? stopLossPaymentInCents : undefined
                      }
                      stopLossCalculation={
                        idx === 0 ? stopLossCalculation : undefined
                      }
                      chargeOutlierPaymentInCents={
                        idx === 0 ? chargeOutlierPaymentInCents : undefined
                      }
                      chargeOutlierCalculation={
                        idx === 0 ? chargeOutlierCalculation : undefined
                      }
                      inpatientOutlierCalculation={
                        idx === 0 ? inpatientOutlierCalculation : undefined
                      }
                      lesserOfChargesCalculation={
                        idx === 0 ? lesserOfChargesCalculation : undefined
                      }
                      sequestrationDiscountInCents={
                        idx === 0
                          ? claim?.sequestrationDiscountInCents
                          : undefined
                      }
                    />
                  </TabPanel>
                );
              })}
            </TabView>

            {/* Claim Allowables Table */}
            {allowableRows.length > 0 && (
              <ClaimAllowablesSection>
                <ClaimAllowablesTitle>Claim Allowables</ClaimAllowablesTitle>
                <DataTable
                  value={allowableRows}
                  dataKey="key"
                  stripedRows
                  responsiveLayout="scroll"
                  style={{ fontSize: "0.9rem" }}
                >
                  <Column
                    field="cptCode"
                    header="Code"
                    style={{ minWidth: "5rem" }}
                  />
                  <Column
                    field="modifier"
                    header="Modifier"
                    body={(row) => row.modifier || "-"}
                    style={{ minWidth: "5rem" }}
                  />
                  <Column
                    field="units"
                    header="Unit"
                    body={(row) => row.units ?? "-"}
                    style={{ minWidth: "4rem" }}
                  />
                  <Column
                    field="allowedInCents"
                    header="Allowed Amount"
                    body={(row) => formatCurrency(row.allowedInCents)}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="contractedRateInCents"
                    header="Contracted Rate"
                    body={(row) =>
                      row.contractedRateInCents != null
                        ? formatCurrency(row.contractedRateInCents)
                        : "-"
                    }
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="method"
                    header="Method"
                    body={(row) => row.method ?? "-"}
                    style={{
                      minWidth: "16rem",
                      whiteSpace: "normal",
                      lineHeight: 1.4,
                    }}
                  />
                  <Column
                    field="expectedInCents"
                    header="Expected"
                    body={(row) =>
                      row.expectedInCents != null
                        ? formatCurrency(row.expectedInCents)
                        : "-"
                    }
                    style={{ minWidth: "7rem" }}
                  />
                  <Column
                    field="varianceInCents"
                    header="Variance"
                    body={(row) => formatVariance(row.varianceInCents)}
                    style={{ minWidth: "7rem" }}
                  />
                </DataTable>
              </ClaimAllowablesSection>
            )}
          </>
        )}
    </>
  );
}
